"use client";

import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { useLocale } from "next-intl";

import type { AppLocale } from "@/i18n/routing";
import { track } from "@/integrations/posthog/events";

import { useAddedToCartPopup, type AddedToCartPopupPayload } from "./AddedToCartPopupProvider";
import { addPhoneStickerToCart, readPhoneUpsellOffer } from "./cart-actions";
import { useCartCount } from "./CartCountProvider";
import {
  PhoneUpsellGateContext,
  type PhoneUpsellStatus,
  type PhoneUpsellStep,
} from "./phone-upsell-gate-context";
import type { PhoneUpsellOffer } from "./phone-upsell-offer";
import { PhoneUpsellDrawer } from "./PhoneUpsellDrawer";

interface GateState {
  open: boolean;
  status: PhoneUpsellStatus;
  /**
   * The added-to-cart popup the click came from, when it did: the offer is
   * then that popup's second step instead of a sheet of its own.
   */
  shownIn: AddedToCartPopupPayload | null;
}

const CLOSED: GateState = { open: false, status: "idle", shownIn: null };

/**
 * The phone-sticker upsell (spec #107), opened on the way to the cart: every
 * cart link goes through `useCartNavigation`, which asks this gate first.
 * Accept adds the sheet and then continues to the cart; decline continues
 * straight there; dismiss stays on the page. An empty cart is offered it too,
 * as a first sheet at ordinary price.
 *
 * The offer is read ahead whenever the cart count settles, so a click opens
 * the popup at once. A click before the read for the current count has
 * landed — or with no offer — goes straight to the cart: the header's cart
 * link never waits on the network. The added-to-cart popup's "Fortsätt" does
 * (`isOfferPending`), so a tap right after the add still gets the offer.
 *
 * Inside `AddedToCartPopupProvider`: a click from its popup ("Fortsätt")
 * turns that sheet into the offer (`addedToCartStep`) rather than stacking a
 * second sheet on it.
 */
export function PhoneUpsellGateProvider({ children }: { children: ReactNode }) {
  const locale = useLocale() as AppLocale;
  const { settledCount, beginCartNavigation } = useCartCount();
  const { addedToCartPopup, closeAddedToCartPopup } = useAddedToCartPopup();
  // The offer with the cart count it was quoted for: right after an add the
  // count moves on while the old quote is still here, and it must not show.
  // `null` until the first read lands.
  const [quoted, setQuoted] = useState<{
    count: number | null;
    offer: PhoneUpsellOffer | null;
  }>({ count: null, offer: null });
  const isOfferPending = quoted.count !== settledCount;
  const offer = isOfferPending ? null : quoted.offer;
  const [{ open, status, shownIn }, setState] = useState<GateState>(CLOSED);
  const setStatus = (next: PhoneUpsellStatus) =>
    setState((current) => ({ ...current, status: next }));
  // Where the intercepted click was going, until accept or decline goes there.
  const pendingHrefRef = useRef<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const count = settledCount;
    readPhoneUpsellOffer().then(
      (next) => {
        if (!cancelled) {
          setQuoted({ count, offer: next });
        }
      },
      () => {
        if (!cancelled) {
          setQuoted({ count, offer: null });
        }
      }
    );
    return () => {
      cancelled = true;
    };
  }, [settledCount]);

  // Back from the cart restores this page from the bfcache with the popup
  // still open and locked mid-navigation. Nothing to resume: close it, and
  // the added-to-cart popup it may be a step of.
  useEffect(() => {
    const onPageShow = (event: PageTransitionEvent) => {
      if (event.persisted) {
        pendingHrefRef.current = null;
        setState(CLOSED);
        closeAddedToCartPopup();
      }
    };
    window.addEventListener("pageshow", onPageShow);
    return () => window.removeEventListener("pageshow", onPageShow);
  }, [closeAddedToCartPopup]);

  const interceptCartNavigation = useCallback(
    (href: string) => {
      if (!offer) {
        return false;
      }
      // Already on the cart (the header's cart link): the offer is for the
      // way there, not a reload of it.
      if (new URL(href, window.location.href).pathname === window.location.pathname) {
        return false;
      }
      pendingHrefRef.current = href;
      setState({ open: true, status: "idle", shownIn: addedToCartPopup });
      track("phone_upsell_shown", offer.props);
      return true;
    },
    [offer, addedToCartPopup]
  );

  // A full document load, as every cart navigation is (`useCartNavigation`).
  const continueToCart = () => {
    const href = pendingHrefRef.current;
    pendingHrefRef.current = null;
    if (!href) {
      return;
    }
    beginCartNavigation();
    window.location.href = href;
  };

  const accept = (phoneNumber: string) => {
    if (!offer) {
      return;
    }
    const offerProps = offer.props;
    setStatus("adding");
    void addPhoneStickerToCart(locale, phoneNumber).then(
      (result) => {
        // Kept open with an inline error rather than continuing: the buyer
        // would otherwise reach the cart believing the sticker is in it.
        if (result.status !== "success") {
          setStatus("add_failed");
          return;
        }
        // Flushed at once: the document is about to go.
        track("phone_upsell_accepted", offerProps, { send_instantly: true });
        setStatus("continuing");
        continueToCart();
      },
      () => setStatus("add_failed")
    );
  };

  const decline = () => {
    if (offer) {
      track(
        "phone_upsell_declined",
        { ...offer.props, method: "button" },
        { send_instantly: true }
      );
    }
    setStatus("continuing");
    continueToCart();
  };

  const dismiss = () => {
    if (offer) {
      track("phone_upsell_declined", { ...offer.props, method: "dismissed" });
    }
    pendingHrefRef.current = null;
    // As a popup's step it stays put: the popup is closing, and its sheet
    // must not turn back into the first step on the way out.
    setState((current) => (current.shownIn ? current : { ...current, open: false }));
  };

  // A step of the popup it was opened from, and of no later one: a new add
  // opens a new popup, which starts at its first step. Held while that popup
  // closes (`null`), so its exit animation keeps showing the offer.
  const addedToCartStep: PhoneUpsellStep | null =
    offer && open && shownIn && (addedToCartPopup === null || addedToCartPopup === shownIn)
      ? {
          status,
          offerPrice: offer.price,
          offerSavings: offer.savings,
          onAccept: accept,
          onDecline: decline,
          onDismiss: dismiss,
        }
      : null;

  return (
    <PhoneUpsellGateContext.Provider
      value={{ interceptCartNavigation, isOfferPending, addedToCartStep }}
    >
      {children}
      {offer ? (
        <PhoneUpsellDrawer
          open={open && shownIn === null}
          status={status}
          offerPrice={offer.price}
          offerSavings={offer.savings}
          onAccept={accept}
          onDecline={decline}
          onDismiss={dismiss}
        />
      ) : null}
    </PhoneUpsellGateContext.Provider>
  );
}
